import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BarChart3, RefreshCw, Users2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  admin: "Admin",
  bank_staff: "Bank Staff",
  telecaller: "Telecaller",
  field_staff: "Field Staff",
  mahalir_manager: "Mahalir Kuzhu Manager",
  office_staff: "Office Staff",
  customer: "Customer",
};

export default function SourceReportPage() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/source-report").then(r => setData(r.data)).catch(e => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-500 p-6">Loading...</div>;
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  return (
    <div className="space-y-6" data-testid="source-report-page">
      <div className="card-surface p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="font-heading text-xl text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-amber-500"/> Role-wise Source Report</div>
            <div className="text-sm text-slate-500">Every lead is tagged with the role &amp; method that added it. Number-only leads counted too.</div>
          </div>
          <Button variant="secondary" onClick={load} data-testid="source-refresh" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
            <RefreshCw className="w-4 h-4 mr-2"/>Refresh
          </Button>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="font-heading text-white mb-4 flex items-center gap-2"><Users2 className="w-4 h-4 text-amber-500"/> Summary by Role</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.by_role.map(r => (
            <div key={r.role} className="bg-slate-950/60 border border-slate-800 rounded-xl p-4" data-testid={`role-card-${r.role}`}>
              <div className="text-xs uppercase tracking-widest text-amber-500 font-bold">{ROLE_LABELS[r.role] || r.role}</div>
              <div className="font-display text-3xl text-white mt-2">{r.cases}</div>
              <div className="text-xs text-slate-500 mt-1">leads · {r.disbursed} disbursed · {r.number_only} number-only</div>
              <div className="text-xs text-slate-400 mt-1">{inr(r.total_amount)} pipeline</div>
            </div>
          ))}
          {data.by_role.length === 0 && <div className="col-span-full text-slate-500 text-sm">No source-tagged leads yet</div>}
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="font-heading text-white mb-4">Breakdown by Role × Source Type</div>
        <Table>
          <TableHeader><TableRow className="border-white/5">
            <TableHead>Role</TableHead>
            <TableHead>Source Type</TableHead>
            <TableHead className="text-right">Leads</TableHead>
            <TableHead className="text-right">Disbursed</TableHead>
            <TableHead className="text-right">Number-Only</TableHead>
            <TableHead className="text-right">Pipeline (₹)</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.rows.map((r, i) => (
              <TableRow key={i} className="border-white/5" data-testid={`source-row-${i}`}>
                <TableCell><Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{ROLE_LABELS[r.source_role] || r.source_role}</Badge></TableCell>
                <TableCell className="text-slate-300 text-sm">{r.source_type || "—"}</TableCell>
                <TableCell className="text-right text-white font-bold">{r.cases}</TableCell>
                <TableCell className="text-right text-emerald-400">{r.disbursed}</TableCell>
                <TableCell className="text-right text-amber-400">{r.number_only}</TableCell>
                <TableCell className="text-right font-mono-alt">{inr(r.total_amount)}</TableCell>
              </TableRow>
            ))}
            {data.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No sourced leads yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
